import React, { useEffect, useId, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  billChipMeta,
  Callout,
  MaskedTaxId,
  payableMeta,
  paymentStatusMeta,
  StatusChip,
  triMeta,
} from "../../components/ui";
import { monoFontFamily } from "../../theme/tokens";
import type { CSITipaltiStaging } from "../../types/CSITypes";
import agent from "../../utils/axios";
import { bucketFor } from "../../utils/billBucket";
import {
  EMPTY_VALUE,
  formatDate,
  formatMoney,
  formatPaymentDate,
} from "../../utils/format";
import {
  DetailItem,
  IdLink,
  IdList,
  RecordDetails,
  RecordLinkHandlers,
  TaxIdValue,
} from "./RecordDetails";
import { ReviewForm } from "./ReviewForm";
import { reviewResultMeta } from "./reviewResults";
import { fullName, rowsWithSameTaxId } from "./stagingData";
import { SectionCaption, TipaltiCompare } from "./TipaltiCompare";

/* ------------------------------------------------------------------ shared bits */

/**
 * Read-only dialog on the theme's dialog look (radius, title and spacing come from the
 * theme). Nested dialogs stack by the order they open, so no z-index is set here.
 */
function InfoDialog({
  open,
  onClose,
  title,
  subtitle,
  maxWidth = "sm",
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg";
  children: React.ReactNode;
}) {
  const titleId = useId();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      aria-labelledby={titleId}
      scroll="paper"
    >
      <DialogTitle component="div">
        <Box component="h2" id={titleId} sx={{ m: 0, font: "inherit" }}>
          {title}
        </Box>
        {subtitle && (
          <Box
            sx={{
              mt: 1,
              fontSize: "0.8125rem",
              fontWeight: 400,
              color: "text.secondary",
            }}
          >
            {subtitle}
          </Box>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 4, py: 4 }}>
        {children}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailGrid({
  children,
  min = 180,
}: {
  children: React.ReactNode;
  min?: number;
}) {
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`,
        columnGap: 4,
        rowGap: 3,
      }}
    >
      {children}
    </Box>
  );
}

function LoadingBlock({ label }: { label: string }) {
  return (
    <Box
      role="status"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        py: 8,
        color: "text.secondary",
        fontSize: "0.8125rem",
      }}
    >
      <CircularProgress size={20} aria-hidden />
      {label}
    </Box>
  );
}

const groupCount = (row: CSITipaltiStaging) => row.groupPayeeIds.length;

/** In Tipalti / Matched / Payable / Active / Group chips for one analysis row. */
export function RowStatusChips({
  row,
  showReview,
}: {
  row: CSITipaltiStaging;
  showReview?: boolean;
}) {
  const groups = groupCount(row);
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
      <StatusChip
        size="small"
        meta={triMeta(row.inTipalti, {
          yes: "In Tipalti",
          no: "Not in Tipalti",
          unknown: "In Tipalti unknown",
        })}
      />
      <StatusChip
        size="small"
        meta={triMeta(row.isMatchedWithTipalti, {
          yes: "Matched",
          no: "Not matched",
          unknown: "Match unknown",
        })}
      />
      <StatusChip
        size="small"
        meta={triMeta(row.isPayable, {
          yes: "Payable",
          no: "Not payable",
          unknown: "Payable unknown",
        })}
      />
      <StatusChip
        size="small"
        meta={triMeta(row.isActive, { yes: "Active", no: "Inactive" })}
      />
      <StatusChip
        size="small"
        tone={groups > 1 ? "warn" : "neutral"}
        label={`Group (${groups})`}
      />
      {showReview && (
        <StatusChip size="small" meta={reviewResultMeta(row.reviewedResults)} />
      )}
    </Box>
  );
}

/* ---------------------------------------------------------- Payee IDs dialog */

/**
 * "Payee IDs" (formerly "Show Payee Details"): the row's identifiers, with the same
 * links as the record details.
 */
export function PayeeIdsDialog({
  row,
  open,
  onClose,
  canRevealTaxId,
  onOpenPayeeDetail,
  onOpenTaxIdMatches,
  onOpenMatchedPayee,
}: {
  row: CSITipaltiStaging;
  open: boolean;
  onClose: () => void;
  canRevealTaxId: boolean;
} & RecordLinkHandlers) {
  return (
    <InfoDialog
      open={open}
      onClose={onClose}
      title="Payee IDs"
      subtitle={row.csiName || undefined}
    >
      <DetailGrid>
        <DetailItem label="Payee ID">
          {row.payeeId ? <IdLink id={row.payeeId} label="" /> : EMPTY_VALUE}
        </DetailItem>
        <DetailItem label="Tax ID">
          <TaxIdValue
            value={row.tin}
            canReveal={canRevealTaxId}
            onOpenMatches={onOpenTaxIdMatches}
          />
        </DetailItem>
        <DetailItem label="Matched payee ID">
          {row.matchedPayeeId ? (
            <IdLink
              id={row.matchedPayeeId}
              onOpen={onOpenMatchedPayee}
              label={`Open matched payee ${row.matchedPayeeId}`}
            />
          ) : (
            "None"
          )}
        </DetailItem>
        <DetailItem label="Group payee IDs">
          <IdList
            ids={row.groupPayeeIds}
            onOpen={onOpenPayeeDetail}
            label={(id) => `Open CSI Pay payee ${id}`}
          />
        </DetailItem>
        <DetailItem label="Additional matched IDs">
          <IdList
            ids={row.additionalMatchedPayeeIds}
            label={(id) => `Payee ${id}`}
          />
        </DetailItem>
        <DetailItem label="Active">
          <StatusChip size="small" meta={triMeta(row.isActive)} />
        </DetailItem>
      </DetailGrid>
    </InfoDialog>
  );
}

/* --------------------------------------------------------------- PayeeModal */

/**
 * The analysis record of one payee (opened from a matched payee ID or from the
 * same-tax-ID list): status chips, the CSI Pay / Tipalti compare, record details and
 * the review form, saved the same way as in the table.
 */
export function PayeeModal({
  item,
  open,
  onClose,
  onSaved,
  canRevealTaxId,
  onOpenPayeeDetail,
  onOpenMatchedPayee,
}: {
  item: CSITipaltiStaging | null;
  open: boolean;
  onClose: () => void;
  onSaved: (row: CSITipaltiStaging) => void;
  canRevealTaxId: boolean;
} & Pick<RecordLinkHandlers, "onOpenPayeeDetail" | "onOpenMatchedPayee">) {
  if (!item) return null;

  return (
    <InfoDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={item.csiName || "Payee"}
      subtitle={
        <Box component="span" sx={{ fontFamily: monoFontFamily }}>
          {item.payeeId}
        </Box>
      }
    >
      <RowStatusChips row={item} showReview />
      <TipaltiCompare row={item} />
      <RecordDetails
        row={item}
        canRevealTaxId={canRevealTaxId}
        onOpenPayeeDetail={onOpenPayeeDetail}
        onOpenMatchedPayee={onOpenMatchedPayee}
      />
      <Box>
        <SectionCaption>Review</SectionCaption>
        <ReviewForm row={item} onSaved={onSaved} />
      </Box>
    </InfoDialog>
  );
}

/* ------------------------------------------------------------ TinMatchModal */

/**
 * The rows on the CURRENT PAGE whose tax ID equals the one clicked (no server lookup).
 * `tin` is the raw row value; it is only ever shown masked.
 */
export function TinMatchModal({
  tin,
  items,
  open,
  onClose,
  onPayeeIdClick,
  canRevealTaxId,
}: {
  tin: string;
  items: CSITipaltiStaging[];
  open: boolean;
  onClose: () => void;
  onPayeeIdClick: (payeeId: string) => void;
  canRevealTaxId: boolean;
}) {
  const matches = rowsWithSameTaxId(items, tin);

  return (
    <InfoDialog
      open={open}
      onClose={onClose}
      title="Records with the same tax ID on this page"
      subtitle={
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 2 }}
        >
          Tax ID <MaskedTaxId value={tin} canReveal={canRevealTaxId} />
        </Box>
      }
    >
      {matches.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No records on this page have this tax ID.
        </Typography>
      ) : (
        <Box
          component="ul"
          sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 3 }}
        >
          {matches.map((row, index) => (
            <Box component="li" key={row.id || index}>
              {index > 0 && <Divider sx={{ mb: 3 }} />}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  columnGap: 3,
                  mb: 1.5,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {row.csiName || "Unnamed payee"}
                </Typography>
                {row.payeeId && (
                  <Typography variant="body2" component="span">
                    <IdLink
                      id={row.payeeId}
                      onOpen={onPayeeIdClick}
                      label={`Open analysis record for ${
                        row.csiName || row.payeeId
                      }`}
                    />
                  </Typography>
                )}
              </Box>
              <RowStatusChips row={row} />
            </Box>
          ))}
        </Box>
      )}
    </InfoDialog>
  );
}

/* --------------------------------------------------------- PayeeDetailModal */

interface PayeeBill {
  requestingApplication?: string | null;
  requestingClient?: string | null;
  programName?: string | null;
  invoiceDate?: string | null;
  amount?: number | null;
  status?: string | null;
  paymentService?: {
    billRefCode?: string | null;
    paymentStatus?: string | null;
  } | null;
}

interface PayeeDetail {
  name?: { first?: string; middle?: string; last?: string } | null;
  type?: string | null;
  last4?: string | null;
  email?: string | null;
  phone?: { home?: string | null; cell?: string | null } | null;
  mailingAddress?: {
    address1?: string | null;
    address2?: string | null;
    address3?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
  } | null;
  payable?: boolean | null;
  reasonNotPayable?: string | null;
  paymentMethod?: string | null;
  bills?: PayeeBill[] | null;
}

/** The live CSI Pay payee record (GET /payees/{id}), loaded only when opened. */
export function PayeeDetailModal({
  payeeId,
  open,
  onClose,
  onBillRefCodeClick,
}: {
  payeeId: string | null;
  open: boolean;
  onClose: () => void;
  onBillRefCodeClick?: (billRefCode: string) => void;
}) {
  const [payee, setPayee] = useState<PayeeDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!payeeId || !open) return;
    let cancelled = false;
    setPayee(null);
    setError(null);
    setLoading(true);
    agent.Payees.getPayee(payeeId)
      .then((response: PayeeDetail) => {
        if (!cancelled) setPayee(response);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load this payee. Try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [payeeId, open]);

  if (!payeeId) return null;

  const name = fullName(payee?.name);
  const bills = Array.isArray(payee?.bills) ? payee?.bills ?? [] : [];
  const address = payee?.mailingAddress;

  return (
    <InfoDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={
        payee ? (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {name || "Payee"}
            <StatusChip
              size="small"
              meta={payableMeta(payee.payable, payee.reasonNotPayable)}
            />
          </Box>
        ) : (
          "Payee"
        )
      }
      subtitle="The payee's record in CSI Pay."
    >
      {loading && <LoadingBlock label="Loading payee…" />}
      {error && (
        <Callout tone="err" role="alert">
          {error}
        </Callout>
      )}
      {payee && !loading && !error && (
        <>
          <Box component="section" aria-label="General">
            <SectionCaption>General</SectionCaption>
            <DetailGrid>
              <DetailItem label="Payee ID">
                <IdLink id={payeeId} label="" />
              </DetailItem>
              <DetailItem label="Type">{payee.type || EMPTY_VALUE}</DetailItem>
              <DetailItem label="Tax ID">
                <MaskedTaxId value={payee.last4} canReveal={false} />
              </DetailItem>
              <DetailItem label="Payment method">
                {payee.paymentMethod || "Not specified"}
              </DetailItem>
            </DetailGrid>
          </Box>

          <Box component="section" aria-label="Contact">
            <SectionCaption>Contact</SectionCaption>
            <DetailGrid>
              <DetailItem label="Name">{name || EMPTY_VALUE}</DetailItem>
              <DetailItem label="Email">
                {payee.email ? (
                  <Link href={`mailto:${payee.email}`}>{payee.email}</Link>
                ) : (
                  EMPTY_VALUE
                )}
              </DetailItem>
              <DetailItem label="Phone">
                {payee.phone?.home || payee.phone?.cell ? (
                  <>
                    {payee.phone?.home && <div>Home: {payee.phone.home}</div>}
                    {payee.phone?.cell && <div>Cell: {payee.phone.cell}</div>}
                  </>
                ) : (
                  "No phone numbers on file"
                )}
              </DetailItem>
              <DetailItem label="Mailing address">
                {address?.address1 ? (
                  <>
                    <div>
                      {[address.address1, address.address2, address.address3]
                        .filter(Boolean)
                        .join(" ")}
                    </div>
                    <div>
                      {[address.city, address.state].filter(Boolean).join(", ")}{" "}
                      {address.postalCode}
                    </div>
                  </>
                ) : (
                  "No address on file"
                )}
              </DetailItem>
            </DetailGrid>
          </Box>

          <Box component="section" aria-label="Bills">
            <SectionCaption>Bills ({bills.length})</SectionCaption>
            {bills.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No bills found for this payee.
              </Typography>
            ) : (
              <TableContainer
                sx={{
                  maxHeight: 400,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Table size="small" stickyHeader aria-label="Payee bills">
                  <TableHead>
                    <TableRow>
                      <TableCell>Bill ref</TableCell>
                      <TableCell>Application · client</TableCell>
                      <TableCell>Program</TableCell>
                      <TableCell>Invoice date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Bill status</TableCell>
                      <TableCell>Payment status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bills.map((bill, index) => {
                      const ref = bill.paymentService?.billRefCode || "";
                      return (
                        // By position too: AB#5377 bills can share a ref code.
                        <TableRow key={`${ref}-${index}`}>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {ref ? (
                              <IdLink
                                id={ref}
                                onOpen={onBillRefCodeClick}
                                label={`Open bill ${ref}`}
                              />
                            ) : (
                              EMPTY_VALUE
                            )}
                          </TableCell>
                          <TableCell>
                            {[bill.requestingApplication, bill.requestingClient]
                              .filter(Boolean)
                              .join(" · ") || EMPTY_VALUE}
                          </TableCell>
                          <TableCell>
                            {bill.programName || EMPTY_VALUE}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {formatDate(bill.invoiceDate)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {formatMoney(bill.amount)}
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              size="small"
                              meta={billChipMeta(
                                bill.status,
                                bill.paymentService?.paymentStatus
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              size="small"
                              meta={paymentStatusMeta(
                                bill.paymentService?.paymentStatus
                              )}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </>
      )}
    </InfoDialog>
  );
}

/* ----------------------------------------------------------- BillDetailModal */

interface BillDetail {
  amount?: number | null;
  status?: string | null;
  payTo?: string | null;
  paymentMethod?: string | null;
  requestingApplication?: string | null;
  requestingClient?: string | null;
  programName?: string | null;
  invoiceDate?: string | null;
  description?: string | null;
  clientBillId?: string | null;
  applicantId?: string | null;
  address?: {
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
  } | null;
  paymentService?: {
    paymentRefCode?: string | null;
    paymentDate?: string | null;
    paymentStatus?: string | null;
  } | null;
}

interface BillResponse {
  name?: { first?: string; middle?: string; last?: string } | null;
  email?: string | null;
  bills?: BillDetail[] | null;
}

/** One bill (GET /Bills/GetByRefcode/{ref}), loaded only when opened. */
export function BillDetailModal({
  billRefCode,
  open,
  onClose,
}: {
  billRefCode: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [billData, setBillData] = useState<BillResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!billRefCode || !open) return;
    let cancelled = false;
    setBillData(null);
    setError(null);
    setLoading(true);
    agent.Bills.getBillByRefCode(billRefCode)
      .then((response: BillResponse) => {
        if (!cancelled) setBillData(response);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load this bill. Try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [billRefCode, open]);

  if (!billRefCode) return null;

  const bill = billData?.bills?.[0];
  const name = fullName(billData?.name);
  // The shared bill-bucket rule (Dashboard, Bills): bill status wins, payment status is
  // only read for legacy rows with no bill status.
  const paid = bill
    ? bucketFor(bill.status, bill.paymentService?.paymentStatus) === "paid"
    : false;
  const amount = formatMoney(bill?.amount);
  const payee = name || "this payee";
  const showPayTo =
    !!bill?.payTo &&
    bill.payTo.trim().toLowerCase() !== name.trim().toLowerCase();
  const address = bill?.address;

  return (
    <InfoDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          Bill {billRefCode}
          {bill && (
            <StatusChip
              size="small"
              meta={billChipMeta(
                bill.status,
                bill.paymentService?.paymentStatus
              )}
            />
          )}
        </Box>
      }
      subtitle={
        bill
          ? paid
            ? `Payment of ${amount} to ${payee}.`
            : `Bill for ${amount} to ${payee}. Not paid yet.`
          : undefined
      }
    >
      {loading && <LoadingBlock label="Loading bill…" />}
      {error && (
        <Callout tone="err" role="alert">
          {error}
        </Callout>
      )}
      {billData && !bill && !loading && !error && (
        <Callout tone="neutral">No bill was found for {billRefCode}.</Callout>
      )}
      {bill && !loading && !error && (
        <>
          <Box component="section" aria-label="Payment">
            <SectionCaption>Payment</SectionCaption>
            <DetailGrid>
              <DetailItem label="Payment ref">
                {bill.paymentService?.paymentRefCode || EMPTY_VALUE}
              </DetailItem>
              <DetailItem label="Payment date">
                {/* A stored date on an unpaid bill is stale: only a paid bill shows it (AC6). */}
                {formatPaymentDate(
                  paid ? bill.paymentService?.paymentDate : null
                )}
              </DetailItem>
              <DetailItem label="Payment type">
                {bill.paymentMethod || EMPTY_VALUE}
              </DetailItem>
              <DetailItem label="Payment status">
                <StatusChip
                  size="small"
                  meta={paymentStatusMeta(bill.paymentService?.paymentStatus)}
                />
              </DetailItem>
            </DetailGrid>
          </Box>

          <Box component="section" aria-label="Paid to and client">
            <DetailGrid min={240}>
              <DetailItem label="Payment to">
                <div>{name || EMPTY_VALUE}</div>
                {showPayTo && <div>{bill.payTo}</div>}
                {address &&
                  (address.address1 ||
                    address.city ||
                    address.state ||
                    address.postalCode) && (
                    <>
                      <div>
                        {[address.address1, address.address2]
                          .filter(Boolean)
                          .join(" ")}
                      </div>
                      <div>
                        {[address.city, address.state]
                          .filter(Boolean)
                          .join(", ")}{" "}
                        {address.postalCode}
                      </div>
                    </>
                  )}
                {billData?.email && (
                  <Link href={`mailto:${billData.email}`}>
                    {billData.email}
                  </Link>
                )}
              </DetailItem>
              <DetailItem label="Client">
                {[
                  bill.requestingApplication,
                  bill.requestingClient,
                  bill.programName,
                ].some(Boolean) ? (
                  <>
                    {bill.requestingApplication && (
                      <div>{bill.requestingApplication}</div>
                    )}
                    {bill.requestingClient && (
                      <div>{bill.requestingClient}</div>
                    )}
                    {bill.programName && <div>{bill.programName}</div>}
                  </>
                ) : (
                  EMPTY_VALUE
                )}
              </DetailItem>
            </DetailGrid>
          </Box>

          <TableContainer sx={{ border: 1, borderColor: "divider" }}>
            <Table size="small" aria-label="Bill amount">
              <TableHead>
                <TableRow>
                  <TableCell>Program</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{bill.programName || EMPTY_VALUE}</TableCell>
                  <TableCell align="right">{amount}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {amount}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box component="section" aria-label="Additional information">
            <SectionCaption>Additional information</SectionCaption>
            <DetailGrid>
              <DetailItem label="Bill status">
                <StatusChip
                  size="small"
                  meta={billChipMeta(
                    bill.status,
                    bill.paymentService?.paymentStatus
                  )}
                />
              </DetailItem>
              <DetailItem label="Invoice date">
                {formatDate(bill.invoiceDate)}
              </DetailItem>
              {bill.clientBillId && (
                <DetailItem label="Client bill ID">
                  {bill.clientBillId}
                </DetailItem>
              )}
              {bill.applicantId && (
                <DetailItem label="Applicant ID">{bill.applicantId}</DetailItem>
              )}
            </DetailGrid>
            {bill.description && (
              <Box component="dl" sx={{ m: 0, mt: 3 }}>
                <DetailItem label="Description">{bill.description}</DetailItem>
              </Box>
            )}
          </Box>
        </>
      )}
    </InfoDialog>
  );
}
